import type {
  QueryClient,
  UseQueryResult,
  UseMutationResult,
} from "@tanstack/react-query";
import {
  EventIdPublic,
  EventParticipantIdPublic,
} from "@shared/schemas/branded-id.schemas";
import type {
  EventParticipantOwnerView,
  EventParticipantSelfView,
} from "@shared/schemas/event-participant-views.schemas";
import type { ParticipantPreferences } from "@shared/schemas/participant-preferences.schemas";
import {
  EventParticipantOwnerViewSchema,
  EventParticipantSelfViewSchema,
} from "@shared/schemas/event-participant-views.schemas";
import { ParticipantPreferencesSchema } from "@shared/schemas/participant-preferences.schemas";
import { paginatedParticipantListSchema } from "@shared/schemas/paginated-participant-list.schemas";
import { resolvePhotoUrl } from "@shared/utils/participant-photo-url.utils";
import {
  ParticipantActivityPageSchema,
  type ParticipantActivityPage,
} from "@shared/schemas/activity-event-views.schemas";
import {
  ParticipantMilestonesSchema,
  type ParticipantMilestones,
} from "@shared/schemas/participant-milestones.schemas";
import type { PaginatedParticipantList } from "@shared/types/participant-data-issue.types";
import type { UpdateParticipantBody } from "@shared/schemas/event-participant-write.schemas";
import { PARTICIPANTS_MODULE_ERROR_MESSAGES } from "@frontend/shared/features/participants/domain/errors/participants-module-error-messages.constants";
import { PhotoUploadError } from "@frontend/shared/features/participants/domain/errors/participants-module.errors";
import { applicationLogger } from "@frontend/shared/infrastructure/logger/application-logger.singleton";
import { BaseApiHooksRepository } from "../../base-api-hooks.repository";
import type {
  GetParticipantsParams,
  ParticipantRepositoryQueryOptions,
} from "@frontend/shared/features/participants/domain/types/participant.types";
import { createParticipantsApi } from "./participants.api";
import type { AxiosInstance } from "axios";
import { PARTICIPANTS_QUERY_KEYS } from "./participants-query-keys.constants";
import { DIRECTORY_QUERY_KEYS } from "../directory/directory-query-keys.constants";
import {
  onParticipantUpdated,
  onParticipantPhotoChanged,
  onParticipantRemoved,
} from "../../cache-actions";
import type {
  RemoveParticipantVariables,
  SetParticipantVipVariables,
} from "./participant.types";
import {
  SeedDemoParticipantsResponseSchema,
  type DemoPersonaSeedCounts,
  type SeedDemoParticipantsResponse,
} from "@frontend/shared/features/participants/domain/types/demo-persona-seed.types";

const ParticipantListResponseSchema = paginatedParticipantListSchema(
  EventParticipantOwnerViewSchema,
);

export class ParticipantHooksHttpRepositoryImpl extends BaseApiHooksRepository {
  private readonly api;

  constructor(queryClient: QueryClient, apiClient: AxiosInstance) {
    super(queryClient);
    this.api = createParticipantsApi(apiClient);
  }

  private toDomain(dto: unknown): EventParticipantSelfView {
    const participant = EventParticipantSelfViewSchema.parse(dto);
    // The API ships the raw stored value and only the client knows the event it
    // resolves against, so every consumer of this view gets it already resolved.
    const photoUrl = resolvePhotoUrl(
      participant.photoUrl?.value,
      EventIdPublic.unwrap(participant.eventIdPublic),
    );
    return {
      ...participant,
      photoUrl: photoUrl === null ? null : { value: photoUrl },
    };
  }

  createHooks() {
    return {
      useGetParticipants: (
        params: GetParticipantsParams,
        options: ParticipantRepositoryQueryOptions = {},
      ): UseQueryResult<
        PaginatedParticipantList<EventParticipantOwnerView>
      > => {
        const { enabled = true } = options;

        return this.query({
          key: PARTICIPANTS_QUERY_KEYS.byParams(params),
          fn: () => this.api.getParticipants(params),
          parse: (response) => ParticipantListResponseSchema.parse(response),
          enabled:
            enabled && EventIdPublic.unwrap(params.eventIdPublic).length > 0,
          staleTime: 30 * 1000, // 30 seconds
          retry: 1,
          // keepPreviousData pattern for pagination - prevents content flash
          placeholderData: (previousData) => previousData,
        });
      },

      fetchParticipants: async (
        params: GetParticipantsParams,
      ): Promise<PaginatedParticipantList<EventParticipantOwnerView>> =>
        ParticipantListResponseSchema.parse(
          await this.api.getParticipants(params),
        ),

      useGetMyParticipant: (
        eventIdPublic: EventIdPublic,
        options: ParticipantRepositoryQueryOptions = {},
      ): UseQueryResult<EventParticipantSelfView> => {
        const { enabled = true } = options;

        return this.query({
          key: PARTICIPANTS_QUERY_KEYS.me(eventIdPublic),
          fn: () => this.api.getMyParticipant(eventIdPublic),
          parse: (dto) => this.toDomain(dto),
          enabled: enabled && EventIdPublic.unwrap(eventIdPublic).length > 0,
          staleTime: 30 * 1000,
          retry: 1,
        });
      },

      useGetByIdPublic: (
        eventIdPublic: EventIdPublic,
        participantIdPublic: EventParticipantIdPublic,
        options: ParticipantRepositoryQueryOptions = {},
      ): UseQueryResult<EventParticipantOwnerView> => {
        const { enabled = true } = options;

        return this.query({
          key: PARTICIPANTS_QUERY_KEYS.byId(
            EventIdPublic.unwrap(eventIdPublic),
            participantIdPublic,
          ),
          fn: () => this.api.getByIdPublic(eventIdPublic, participantIdPublic),
          parse: (dto) => EventParticipantOwnerViewSchema.parse(dto),
          enabled:
            enabled &&
            EventIdPublic.unwrap(eventIdPublic).length > 0 &&
            EventParticipantIdPublic.unwrap(participantIdPublic).length > 0,
          staleTime: 30 * 1000,
          retry: 1,

          initialData: (): EventParticipantOwnerView | undefined => {
            const cachedQueries = this.queryClient.getQueriesData<
              PaginatedParticipantList<EventParticipantOwnerView>
            >({ queryKey: PARTICIPANTS_QUERY_KEYS.list });

            for (const [, data] of cachedQueries) {
              if (data?.items === undefined) continue;
              const found = data.items.find(
                (p) => p.idPublic === participantIdPublic,
              );
              if (found !== undefined) {
                return found;
              }
            }
            return undefined;
          },
        });
      },

      useUpdateParticipant: (): UseMutationResult<
        EventParticipantSelfView,
        Error,
        {
          readonly eventIdPublic: EventIdPublic;
          readonly participantIdPublic: EventParticipantIdPublic;
          readonly data: UpdateParticipantBody;
        }
      > =>
        this.mutation({
          fn: (input: {
            readonly eventIdPublic: EventIdPublic;
            readonly participantIdPublic: EventParticipantIdPublic;
            readonly data: UpdateParticipantBody;
          }) =>
            this.api.updateParticipant(
              input.eventIdPublic,
              input.participantIdPublic,
              input.data,
            ),
          parse: (dto) => this.toDomain(dto),
          onSuccess: (updatedParticipant): void => {
            // Only the user editing their own profile (read via `/me`) calls
            // this. Invalidate `/me` so the edit reflects; do NOT cross-write
            // the full participant into the owner list cache (different shape
            // and audience — the host list refetches on its own).
            void this.queryClient.invalidateQueries({
              queryKey: PARTICIPANTS_QUERY_KEYS.me(
                updatedParticipant.eventIdPublic,
              ),
            });

            onParticipantUpdated(
              this.queryClient,
              updatedParticipant.eventIdPublic,
            );
          },
          onError: (error) => {
            applicationLogger.error(
              "Failed to update participant",
              "ParticipantHooksHttpRepository.useUpdateParticipant",
              error,
            );
          },
        }),

      useGetPreferences: (input: {
        readonly eventIdPublic: EventIdPublic;
        readonly participantIdPublic: EventParticipantIdPublic;
        readonly enabled?: boolean;
      }): UseQueryResult<ParticipantPreferences> => {
        const { eventIdPublic, participantIdPublic, enabled = true } = input;

        return this.query({
          key: PARTICIPANTS_QUERY_KEYS.preferences(participantIdPublic),
          fn: () => this.api.getPreferences(eventIdPublic, participantIdPublic),
          parse: (dto) => ParticipantPreferencesSchema.parse(dto),
          enabled:
            enabled &&
            EventParticipantIdPublic.unwrap(participantIdPublic).length > 0,
          staleTime: 5 * 60_000,
        });
      },

      useUpdatePreferences: (): UseMutationResult<
        ParticipantPreferences,
        Error,
        {
          readonly eventIdPublic: EventIdPublic;
          readonly participantIdPublic: EventParticipantIdPublic;
          readonly data: ParticipantPreferences;
        }
      > =>
        this.mutation({
          fn: (input: {
            readonly eventIdPublic: EventIdPublic;
            readonly participantIdPublic: EventParticipantIdPublic;
            readonly data: ParticipantPreferences;
          }) =>
            this.api.updatePreferences(
              input.eventIdPublic,
              input.participantIdPublic,
              input.data,
            ),
          parse: (dto) => ParticipantPreferencesSchema.parse(dto),
          onSuccess: (data, variables): void => {
            this.queryClient.setQueryData(
              PARTICIPANTS_QUERY_KEYS.preferences(
                variables.participantIdPublic,
              ),
              data,
            );
          },
          onError: (error) => {
            applicationLogger.error(
              "Failed to update preferences",
              "ParticipantHooksHttpRepository.useUpdatePreferences",
              error,
            );
          },
        }),

      useRemovePhoto: (): UseMutationResult<
        void,
        Error,
        {
          readonly eventIdPublic: EventIdPublic;
          readonly participantIdPublic: EventParticipantIdPublic;
        }
      > =>
        this.voidMutation({
          fn: async (input: {
            readonly eventIdPublic: EventIdPublic;
            readonly participantIdPublic: EventParticipantIdPublic;
          }) => {
            await this.api.removePhoto(
              input.eventIdPublic,
              input.participantIdPublic,
            );
          },
          onSuccess: (_data, variables) => {
            onParticipantPhotoChanged(
              this.queryClient,
              variables.eventIdPublic,
              variables.participantIdPublic,
            );
          },
        }),

      useUploadPhoto: (): UseMutationResult<
        void,
        Error,
        {
          readonly eventIdPublic: EventIdPublic;
          readonly participantIdPublic: EventParticipantIdPublic;
          readonly file: File;
        }
      > =>
        this.voidMutation({
          fn: async (input: {
            readonly eventIdPublic: EventIdPublic;
            readonly participantIdPublic: EventParticipantIdPublic;
            readonly file: File;
          }) => {
            const { eventIdPublic, participantIdPublic, file } = input;
            const { uploadUrl, photoKey } =
              await this.api.requestPhotoUploadUrl(
                eventIdPublic,
                participantIdPublic,
                file.type,
              );
            const { etag } = await this.api.putFileToUrl(uploadUrl, file);
            if (etag === null) {
              throw new PhotoUploadError(
                PARTICIPANTS_MODULE_ERROR_MESSAGES.PHOTO_UPLOAD_MISSING_ETAG,
              );
            }
            await this.api.confirmPhotoUpload(
              eventIdPublic,
              participantIdPublic,
              etag,
              photoKey,
            );
          },
          onSuccess: (_data, variables) => {
            onParticipantPhotoChanged(
              this.queryClient,
              variables.eventIdPublic,
              variables.participantIdPublic,
            );
          },
        }),

      useOptInRemember: (): UseMutationResult<
        void,
        Error,
        {
          readonly eventIdPublic: EventIdPublic;
          readonly participantIdPublic: EventParticipantIdPublic;
        }
      > =>
        this.voidMutation({
          fn: async (input: {
            readonly eventIdPublic: EventIdPublic;
            readonly participantIdPublic: EventParticipantIdPublic;
          }) => {
            await this.api.optInRemember(
              input.eventIdPublic,
              input.participantIdPublic,
            );
          },
          onSuccess: (_data, variables): void => {
            const queryKey = PARTICIPANTS_QUERY_KEYS.byId(
              EventIdPublic.unwrap(variables.eventIdPublic),
              variables.participantIdPublic,
            );
            const cached =
              this.queryClient.getQueryData<EventParticipantOwnerView>(
                queryKey,
              );
            if (cached !== undefined) {
              // Safe here because invalidateQueries refetches immediately and only `isolated` is read from this interim object.
              this.queryClient.setQueryData(queryKey, {
                ...cached,
                isolated: false,
              });
            }

            void this.queryClient.invalidateQueries({
              queryKey: PARTICIPANTS_QUERY_KEYS.all,
            });

            void this.queryClient.invalidateQueries({
              queryKey: DIRECTORY_QUERY_KEYS.event(variables.eventIdPublic),
            });
          },
        }),

      useGetActivityLog: (
        eventIdPublic: EventIdPublic,
        participantIdPublic: EventParticipantIdPublic,
        pagination: { readonly limit?: number } = {},
      ) =>
        this.query<unknown, ParticipantActivityPage>({
          key: PARTICIPANTS_QUERY_KEYS.activityLog(
            eventIdPublic,
            participantIdPublic,
            pagination.limit,
          ),
          fn: () =>
            this.api.getActivityLog(
              eventIdPublic,
              participantIdPublic,
              pagination,
            ),
          parse: (data) => ParticipantActivityPageSchema.parse(data),
          enabled:
            EventIdPublic.unwrap(eventIdPublic).length > 0 &&
            EventParticipantIdPublic.unwrap(participantIdPublic).length > 0,
          staleTime: 60 * 1000, // 1 minute
          retry: 1,
        }),

      useGetParticipantMilestones: (
        eventIdPublic: EventIdPublic,
        participantIdPublic: EventParticipantIdPublic,
        options: ParticipantRepositoryQueryOptions = {},
      ): UseQueryResult<ParticipantMilestones> => {
        const { enabled = true } = options;

        return this.query({
          key: PARTICIPANTS_QUERY_KEYS.milestones(
            eventIdPublic,
            participantIdPublic,
          ),
          fn: () => this.api.getMilestones(eventIdPublic, participantIdPublic),
          parse: (data) => ParticipantMilestonesSchema.parse(data),
          enabled:
            enabled &&
            EventIdPublic.unwrap(eventIdPublic).length > 0 &&
            EventParticipantIdPublic.unwrap(participantIdPublic).length > 0,
          staleTime: 60 * 1000,
          retry: 1,
        });
      },

      useSetParticipantVip: () =>
        this.voidMutation<SetParticipantVipVariables>({
          fn: ({ eventIdPublic, participantIdPublic, vip }) =>
            this.api.setParticipantVip(eventIdPublic, participantIdPublic, vip),
          onSuccess: (_data, variables) => {
            void this.queryClient.invalidateQueries({
              queryKey: PARTICIPANTS_QUERY_KEYS.byParams({
                eventIdPublic: variables.eventIdPublic,
              }),
            });
            void this.queryClient.invalidateQueries({
              queryKey: PARTICIPANTS_QUERY_KEYS.byId(
                EventIdPublic.unwrap(variables.eventIdPublic),
                variables.participantIdPublic,
              ),
            });
          },
          onError: (error) => {
            applicationLogger.error(
              "Failed to set participant vip",
              "ParticipantHooksHttpRepository.useSetParticipantVip",
              error,
            );
          },
        }),

      useRemoveParticipant: () =>
        this.mutation<
          { action: string },
          { action: string },
          RemoveParticipantVariables
        >({
          fn: ({ eventIdPublic, participantIdPublic }) =>
            this.api.removeParticipant(eventIdPublic, participantIdPublic),
          parse: (data) => data,
          onSuccess: (_data, variables) => {
            onParticipantRemoved(
              this.queryClient,
              variables.eventIdPublic,
              variables.participantIdPublic,
            );
          },
          onError: (error) => {
            applicationLogger.error(
              "Failed to remove participant",
              "ParticipantHooksHttpRepository.useRemoveParticipant",
              error,
            );
          },
        }),

      useSeedDemoParticipants: (): UseMutationResult<
        SeedDemoParticipantsResponse,
        Error,
        {
          readonly eventIdPublic: EventIdPublic;
          readonly counts: DemoPersonaSeedCounts;
        }
      > =>
        this.mutation({
          fn: (input: {
            readonly eventIdPublic: EventIdPublic;
            readonly counts: DemoPersonaSeedCounts;
          }) =>
            this.api.seedDemoParticipants(input.eventIdPublic, input.counts),
          parse: (dto) => SeedDemoParticipantsResponseSchema.parse(dto),
          onSuccess: (): void => {
            this.invalidateQueries(PARTICIPANTS_QUERY_KEYS.list);
          },
          onError: (error) => {
            applicationLogger.error(
              "Failed to seed demo participants",
              "ParticipantHooksHttpRepository.useSeedDemoParticipants",
              error,
            );
          },
        }),
    };
  }
}
